import { type UseMutationOptions, useMutation } from '@tanstack/react-query';

import {
  insuranceV3CardUploadUrlMutation,
  insuranceV3PutCardsMutation,
} from '@/api/generated/@tanstack/react-query.gen';
import type {
  InsuranceV3CardUploadUrlData,
  InsuranceV3PutCardsResponse,
} from '@/api/generated/types.gen';

export const INSURANCE_CARD_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'application/pdf',
] as const satisfies ReadonlyArray<InsuranceV3CardUploadUrlData['body']['content_type']>;

export type InsuranceCardContentType = (typeof INSURANCE_CARD_CONTENT_TYPES)[number];

type MutationParams = {
  verificationKey: string;
  frontFile: File;
  backFile: File;
};

type UploadedCardSide = {
  cardObjectKey: string;
};

type MutationOptions = Omit<
  UseMutationOptions<InsuranceV3PutCardsResponse, Error, MutationParams>,
  'mutationFn' | 'retry'
>;

export function getInsuranceCardContentType(file: File): InsuranceCardContentType | null {
  return INSURANCE_CARD_CONTENT_TYPES.find((contentType) => contentType === file.type) ?? null;
}

async function getInsuranceCardUploadBody(file: File): Promise<BodyInit> {
  if (import.meta.env.VITE_ENABLE_MOCKS === 'true') {
    return file.arrayBuffer();
  }

  return file;
}

export function useInsuranceCardUploadMutation(options?: MutationOptions) {
  const createCardUploadUrlMutation = useMutation({
    ...insuranceV3CardUploadUrlMutation(),
    retry: false,
  });
  const putCardsMutation = useMutation({
    ...insuranceV3PutCardsMutation(),
    retry: false,
  });

  async function uploadCardSide({
    file,
    side,
    verificationKey,
  }: {
    file: File;
    side: InsuranceV3CardUploadUrlData['body']['side'];
    verificationKey: InsuranceV3CardUploadUrlData['body']['verification_key'];
  }): Promise<UploadedCardSide> {
    const contentType = getInsuranceCardContentType(file);

    if (!contentType) {
      throw new Error(
        `Unsupported insurance card file type: ${file.type}. Supported types: ${INSURANCE_CARD_CONTENT_TYPES.join(', ')}`
      );
    }

    const uploadUrl = await createCardUploadUrlMutation.mutateAsync({
      body: {
        verification_key: verificationKey,
        side,
        content_type: contentType,
      },
    });

    const uploadResponse = await fetch(uploadUrl.presigned_put_url, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: await getInsuranceCardUploadBody(file),
    });

    if (!uploadResponse.ok) {
      throw new Error(`Failed to upload ${side} insurance card`);
    }

    return { cardObjectKey: uploadUrl.s3_object_key };
  }

  return useMutation({
    ...options,
    mutationFn: async ({ verificationKey, frontFile, backFile }) => {
      const [frontUpload, backUpload] = await Promise.all([
        uploadCardSide({
          file: frontFile,
          side: 'front',
          verificationKey,
        }),
        uploadCardSide({
          file: backFile,
          side: 'back',
          verificationKey,
        }),
      ]);

      return putCardsMutation.mutateAsync({
        path: { verification_key: verificationKey },
        body: {
          card_front: frontUpload.cardObjectKey,
          card_back: backUpload.cardObjectKey,
        },
      });
    },
    retry: false,
  });
}
